// PlansTab — Bible › Plans (docs/designs/PlansTab.dc.html, docs/FIX-PLANS.md §2).
//
// Four sections:
//   1. Your plans           — joined, not finished, across any church (home
//                              or followed). Falls back to a join prompt for
//                              this week's plan if nothing's joined yet.
//   2. More from [home]      — the home church's other plans (not currently
//                              being worked on), newest first. Join / Review.
//   3. From churches you follow — same row shape, non-home churches only.
//   4. Series plans           — study_plans, "Coming soon" until that
//                              feature is built.
//
// Props:
//   churchId           — member's home church id (profile.church_id)
//   churchName         — for empty-state / "More from X" copy
//   followedChurchIds  — home + followed church ids (useSermons-style array)
//   churchById         — Map<churchId, church row> for names/logos
//   userId             — current user's id
//   isModerator        — elder/admin/super_admin: shows the empty-state "Set
//                         up weekly plans" link (generating a plan itself
//                         happens from the sermon sheet or Leaders dashboard,
//                         not here — docs/FIX-PLANS.md §2)
//   onOpenPlanDay(plan, follow, dayNumber) — open ReadingPlanDaySheet
//   onOpenSermon(sermon)                   — open the sermon sheet for the source sermon
//   onOpenLeaders()                        — open the Leaders dashboard
//   onFindChurch()                         — no home church yet — find one

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";

const GOLD = "var(--gold)";
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";
const SERIES_PASTELS = [
  { bg: "var(--lavender)", ink: "var(--lavender-ink)" },
  { bg: "var(--sage)", ink: "var(--sage-ink)" },
  { bg: "var(--peach)", ink: "var(--peach-ink)" },
  { bg: "var(--sky)", ink: "var(--sky-ink)" },
];

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d + (d.length <= 10 ? "T00:00:00" : "")).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return d; }
}

export default function PlansTab({ churchId, churchName, followedChurchIds = [], churchById = new Map(), userId, isModerator, onOpenPlanDay, onOpenSermon, onOpenLeaders, onFindChurch }) {
  const [plans, setPlans] = useState([]);
  const [followsByPlan, setFollowsByPlan] = useState(new Map());
  const [completedCounts, setCompletedCounts] = useState(new Map()); // plan_id -> count
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [seeAllSection, setSeeAllSection] = useState(null); // 'home' | 'followed' | null
  const [seriesPlans, setSeriesPlans] = useState([]);

  const scopeIds = useMemo(() => [...new Set((followedChurchIds || []).filter(Boolean))], [followedChurchIds]);
  const scopeKey = scopeIds.slice().sort().join(",");

  const loadPlans = useCallback(async () => {
    if (scopeIds.length === 0) { setPlans([]); setLoading(false); return; }
    setLoading(true);
    const { data: planRows } = await supabase
      .from("reading_plans")
      .select("*, sermons(id, title, display_title, preacher, date, youtube_id, youtube_url, duration, church_id), reading_plan_days(*)")
      .in("church_id", scopeIds)
      .eq("status", "published")
      .order("published_at", { ascending: false });
    const list = (planRows || []).map(p => ({
      ...p,
      reading_plan_days: (p.reading_plan_days || []).slice().sort((a, b) => a.day_number - b.day_number),
    }));

    let follows = [];
    if (userId && list.length) {
      const { data: followRows } = await supabase
        .from("reading_plan_follows")
        .select("*")
        .eq("user_id", userId)
        .in("plan_id", list.map(p => p.id));
      follows = followRows || [];
    }
    setPlans(list);
    setFollowsByPlan(new Map(follows.map(f => [f.plan_id, f])));

    // "n people did this" — counts-only RPC per plan (reading_plan_follows is
    // own-row-only RLS), one call per plan since there's no bulk variant yet.
    const counts = new Map();
    await Promise.all(list.map(async (p) => {
      const { data } = await supabase.rpc("reading_plan_completed_count", { p_plan_id: p.id });
      counts.set(p.id, data || 0);
    }));
    setCompletedCounts(counts);
    setLoading(false);
  }, [scopeKey, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPlans(); }, [loadPlans]);

  useEffect(() => {
    supabase.from("study_plans").select("id, title, description, church_id, estimated_weeks")
      .in("church_id", scopeIds.length ? scopeIds : ["00000000-0000-0000-0000-000000000000"])
      .then(({ data }) => setSeriesPlans(data || []));
  }, [scopeKey]);

  const { yourPlans, homePlans, followedPlans } = useMemo(() => {
    const yourPlans = [], homePlans = [], followedPlans = [];
    for (const plan of plans) {
      const follow = followsByPlan.get(plan.id);
      const done = (follow?.days_completed || []).length >= 5;
      if (follow && !done) { yourPlans.push(plan); continue; }
      (plan.church_id === churchId ? homePlans : followedPlans).push(plan);
    }
    return { yourPlans, homePlans, followedPlans };
  }, [plans, followsByPlan, churchId]);

  async function startPlan(plan) {
    if (!userId || starting) return;
    setStarting(plan.id);
    const { data } = await supabase
      .from("reading_plan_follows")
      .upsert({ plan_id: plan.id, user_id: userId, last_opened_day: 1 }, { onConflict: "plan_id,user_id" })
      .select().single();
    setFollowsByPlan(m => new Map(m).set(plan.id, data));
    setStarting(null);
    onOpenPlanDay?.(plan, data, 1);
  }

  if (!churchId && scopeIds.length === 0) {
    return (
      <div style={S.wrap}>
        <div style={S.empty}>Join a church to see its reading plans.</div>
      </div>
    );
  }

  if (loading) {
    return <div style={S.wrap}><div style={S.empty}>Loading…</div></div>;
  }

  const visibleHomePlans = seeAllSection === "home" ? homePlans : homePlans.slice(0, 3);
  const visibleFollowedPlans = seeAllSection === "followed" ? followedPlans : followedPlans.slice(0, 3);

  return (
    <div style={S.wrap}>
      {yourPlans.length === 0 && homePlans.length === 0 && followedPlans.length === 0 && (
        <div style={S.emptyCard}>
          <div style={S.emptyIcon}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5zM4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>
          </div>
          <div style={S.emptyTitle}>No plans yet</div>
          {churchId ? (
            <div style={S.emptyBody}>When {churchName || "your church"}'s next sermon comes in, a 5-day plan from it will appear here.</div>
          ) : (
            <>
              <div style={S.emptyBody}>Find your church to see its reading plans.</div>
              <button onClick={onFindChurch} style={S.emptyBtn}>Find your church</button>
            </>
          )}
          {isModerator && churchId && (
            <button onClick={onOpenLeaders} style={S.emptyLeadersLink}>Set up weekly plans →</button>
          )}
        </div>
      )}

      {yourPlans.length > 0 && (
        <Section title="Your plans">
          {yourPlans.map(plan => {
            const follow = followsByPlan.get(plan.id);
            const done = (follow?.days_completed || []).length;
            const church = churchById.get(plan.church_id);
            return (
              <YourPlanCard key={plan.id} plan={plan} church={church} done={done}
                onContinue={() => onOpenPlanDay?.(plan, follow, follow?.last_opened_day || 1)}/>
            );
          })}
        </Section>
      )}

      {homePlans.length > 0 && (
        <Section title={`More from ${churchName || "your church"}`} seeAll={homePlans.length > 3 && seeAllSection !== "home" ? () => setSeeAllSection("home") : null} seeAllCount={homePlans.length}>
          {visibleHomePlans.map(plan => (
            <PastPlanRow key={plan.id} plan={plan} follow={followsByPlan.get(plan.id)} peopleCount={completedCounts.get(plan.id) || 0}
              onJoin={() => startPlan(plan)} onReview={() => onOpenPlanDay?.(plan, followsByPlan.get(plan.id), 1)}
              busy={starting === plan.id}/>
          ))}
        </Section>
      )}

      {followedPlans.length > 0 && (
        <Section title="From churches you follow" seeAll={followedPlans.length > 3 && seeAllSection !== "followed" ? () => setSeeAllSection("followed") : null} seeAllCount={followedPlans.length}>
          {visibleFollowedPlans.map(plan => (
            <PastPlanRow key={plan.id} plan={plan} follow={followsByPlan.get(plan.id)} peopleCount={completedCounts.get(plan.id) || 0}
              churchName={churchById.get(plan.church_id)?.name}
              onJoin={() => startPlan(plan)} onReview={() => onOpenPlanDay?.(plan, followsByPlan.get(plan.id), 1)}
              busy={starting === plan.id}/>
          ))}
        </Section>
      )}

      {seriesPlans.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={S.seriesHeadRow}>
            <span style={S.sectionTitle}>Series plans</span>
            <span style={S.comingSoon}>Coming soon</span>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {seriesPlans.map((sp, i) => {
              const pastel = SERIES_PASTELS[i % SERIES_PASTELS.length];
              return (
                <div key={sp.id} style={{ ...S.seriesCard, background: pastel.bg, color: pastel.ink }}>
                  <div style={S.seriesCardTitle}>{sp.title}</div>
                  <div style={S.seriesCardSub}>{[sp.estimated_weeks ? `${sp.estimated_weeks} weeks` : null, churchById.get(sp.church_id)?.name].filter(Boolean).join(" · ")}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, seeAll, seeAllCount }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <div style={S.sectionTitle}>{title}</div>
        {seeAll && <button onClick={seeAll} style={S.seeAllLink}>See all {seeAllCount}</button>}
      </div>
      {children}
    </div>
  );
}

// "Your plans" — joined, in progress. Thumbnail, title, "This week · church · n of 5 done", progress bar, Continue.
function YourPlanCard({ plan, church, done, onContinue }) {
  const sermon = plan.sermons || {};
  const thumb = sermon.youtube_id ? `https://i.ytimg.com/vi/${sermon.youtube_id}/mqdefault.jpg` : null;
  return (
    <div style={S.yourPlanCard}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <div style={S.yourPlanThumbWrap}>
          {thumb ? <img src={thumb} alt="" style={S.thumbImg}/> : <div style={{ ...S.thumbImg, background: "var(--border)" }}/>}
          <span style={S.yourPlanPlay}>▶</span>
          <span style={S.yourPlanDayBadge}>Day {done + 1}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={S.yourPlanTitle}>{plan.title}</div>
          <div style={S.yourPlanMeta}>This week · {church?.name || ""} · {done} of 5 done</div>
          <div style={S.progressTrack}><div style={{ ...S.progressFill, width: `${(done / 5) * 100}%` }}/></div>
        </div>
      </div>
      <button onClick={onContinue} style={S.continueBtn}>Continue · Day {done + 1}</button>
    </div>
  );
}

// "More from X" / "From churches you follow" — thumbnail + date badge, title,
// "[passage] · 5 days", "n people did this" / "You finished this ✓", Join/Review.
function PastPlanRow({ plan, follow, peopleCount, churchName, onJoin, onReview, busy }) {
  const sermon = plan.sermons || {};
  const thumb = sermon.youtube_id ? `https://i.ytimg.com/vi/${sermon.youtube_id}/mqdefault.jpg` : null;
  const done = (follow?.days_completed || []).length >= 5;
  const passage = plan.reading_plan_days?.[0]?.passage_ref || "";
  return (
    <div style={S.pastRow}>
      <div style={S.pastThumbWrap}>
        {thumb ? <img src={thumb} alt="" style={S.thumbImg}/> : <div style={{ ...S.thumbImg, background: "var(--border)" }}/>}
        <span style={S.yourPlanPlay}>▶</span>
        <span style={S.pastDateBadge}>{fmtDate(sermon.date)}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={S.pastTitle}>{plan.title}</div>
        <div style={S.pastMeta}>{[passage, "5 days"].filter(Boolean).join(" · ")}</div>
        <div style={S.pastStat}>{done ? "You finished this ✓" : `${peopleCount} people did this`}</div>
        {churchName && <div style={S.pastChurch}>{churchName}</div>}
      </div>
      <button onClick={done ? onReview : onJoin} disabled={busy} style={S.pastActionBtn}>{busy ? "…" : done ? "Review" : "Join"}</button>
    </div>
  );
}

const S = {
  wrap: { flex: 1, overflowY: "auto", padding: "12px 22px calc(env(safe-area-inset-bottom) + 24px)", fontFamily: SANS, boxSizing: "border-box" },
  empty: { fontSize: 13.5, color: "var(--stone)", padding: "30px 4px", textAlign: "center" },
  emptyCard: { background: "var(--white)", borderRadius: 22, padding: "32px 24px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 6 },
  emptyIcon: { width: 48, height: 48, borderRadius: 24, background: "rgba(232,200,122,0.16)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 6 },
  emptyTitle: { fontSize: 17, fontWeight: 800, color: "var(--ink)" },
  emptyBody: { fontSize: 13.5, color: "var(--stone)", lineHeight: 1.5, maxWidth: 260 },
  emptyBtn: { marginTop: 8, background: GOLD, border: "none", color: "var(--ink)", borderRadius: 999, padding: "11px 22px", fontSize: 13.5, fontWeight: 800, cursor: "pointer", fontFamily: SANS },
  emptyLeadersLink: { marginTop: 10, background: "none", border: "none", color: "var(--gold-ink)", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: 0 },
  sectionTitle: { fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--gold-ink)" },
  seeAllLink: { background: "none", border: "none", color: "var(--gold-ink)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: 0 },

  thumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

  yourPlanCard: { background: "var(--white)", borderRadius: 22, padding: 14, display: "flex", flexDirection: "column", gap: 12, marginBottom: 10 },
  yourPlanThumbWrap: { position: "relative", width: 64, height: 64, borderRadius: 14, overflow: "hidden", flexShrink: 0, background: "var(--border)" },
  yourPlanPlay: { position: "absolute", left: "50%", top: "50%", width: 22, height: 22, marginLeft: -11, marginTop: -11, borderRadius: 11, background: "rgba(255,255,255,0.94)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9 },
  yourPlanDayBadge: { position: "absolute", right: 4, bottom: 4, background: "rgba(31,27,22,0.78)", color: "#fff", borderRadius: 5, padding: "1px 5px", fontSize: 9.5, fontWeight: 700 },
  yourPlanTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  yourPlanMeta: { fontSize: 12, color: "var(--stone)" },
  progressTrack: { height: 6, borderRadius: 3, background: "var(--border)", overflow: "hidden" },
  progressFill: { height: "100%", background: "#7C6BC4" },
  continueBtn: { textAlign: "center", background: GOLD, border: "none", borderRadius: 999, padding: "11px 0", fontSize: 14, fontWeight: 800, color: "var(--ink)", cursor: "pointer", fontFamily: SANS },

  pastRow: { display: "flex", gap: 12, alignItems: "center", background: "var(--white)", borderRadius: 18, padding: 10, marginBottom: 8 },
  pastThumbWrap: { position: "relative", width: 64, height: 64, borderRadius: 14, overflow: "hidden", flexShrink: 0, background: "var(--border)" },
  pastDateBadge: { position: "absolute", right: 4, bottom: 4, background: "rgba(31,27,22,0.78)", color: "#fff", borderRadius: 5, padding: "1px 5px", fontSize: 9.5, fontWeight: 700 },
  pastTitle: { fontSize: 14.5, fontWeight: 800, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  pastMeta: { fontSize: 12, color: "var(--stone)" },
  pastStat: { fontSize: 11.5, color: "var(--lavender-ink)", fontWeight: 700 },
  pastChurch: { fontSize: 11, color: "var(--stone)" },
  pastActionBtn: { flexShrink: 0, border: `1.5px solid ${GOLD}`, background: "none", borderRadius: 999, padding: "6px 14px", fontSize: 12.5, fontWeight: 800, color: "var(--ink)", cursor: "pointer", fontFamily: SANS },

  seriesHeadRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  comingSoon: { fontSize: 12, fontWeight: 700, color: "var(--stone)" },
  seriesCard: { flex: "1 1 140px", borderRadius: 20, padding: 14, display: "flex", flexDirection: "column", gap: 4 },
  seriesCardTitle: { fontSize: 15, fontWeight: 800 },
  seriesCardSub: { fontSize: 12, fontWeight: 600 },

};
